import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import {
    Fields,
    TreeSelect,
    SingleSelect,
    HorizontalFlex,
    FieldLabel,
    ButtonWithIconSecondary,
    icons,
} from 'smartsuite-ui';
import { predicateValues } from '../constants/constants';
import { useLabelContext } from '../hooks/labelContext';
import { getPredicateOptions, buildFilterWarning } from './filtersModalUtils';

/* eslint-disable complexity */
export default function FilterTreeSelect(props) {
    const { dashboardLabels, predicateLabels, translationLabels } =
        useLabelContext();

    const predicateOptions = getPredicateOptions(
        predicateLabels,
        props.availableFilter.predicateOptions
    );
    const value = props.filter?.values ?? [];
    const placeholder =
        props.filter?.predicate?.valueField === predicateValues.notIn
            ? translationLabels.none
            : translationLabels.all;

    function handleFilterPredicateChange(predicate) {
        props.updateFilter(
            {
                [props.filterKey]: {
                    values: [],
                    ...props.filter,
                    predicate,
                },
            },
            props.availableFilter.cascadeChildKey
        );
    }

    function handleFilterValueChange(values) {
        props.updateFilter(
            {
                [props.filterKey]: {
                    predicate: predicateOptions[0],
                    ...props.filter,
                    values: values ?? [],
                },
            },
            props.availableFilter.cascadeChildKey
        );
    }

    function renderFilterLabel() {
        const label =
            props.showAsterisk && props.isRequired
                ? `${props.availableFilter.label}*`
                : props.availableFilter.label;

        if (!props.isAdditionalFilter) {
            return label;
        }

        return (
            <HorizontalFlex>
                <FieldLabel>{label}</FieldLabel>
                <ButtonWithIconSecondary
                    iconClass={icons.x}
                    onClick={props.removeAdditionalFilter(props.filterKey)}
                />
            </HorizontalFlex>
        );
    }

    return (
        <Fields
            cssClassModifiers={[
                'no-background',
                'no-pad-top-bottom',
                'no-wrap',
                props.isEditor ? 'column' : '',
            ]}
        >
            <SingleSelect
                disabled={props.disabled}
                isClearable={false}
                label={renderFilterLabel()}
                menuPortalTarget={props.isEditor ? document.body : null}
                onChange={handleFilterPredicateChange}
                options={predicateOptions}
                styles={{
                    menuPortal: (styles) => ({
                        ...styles,
                        zIndex: 10,
                    }),
                }}
                textField="textField"
                valueField="valueField"
                value={props.filter?.predicate ?? predicateOptions[0]}
            />
            <TreeSelect
                disabled={props.disabled}
                hasSelectAllOption={false}
                isValid={!props.isRequired || !isEmpty(value)}
                onChange={handleFilterValueChange}
                options={props.options}
                placeholder={placeholder}
                textField="textField"
                valueField="valueField"
                value={value}
            />
            {props.availableFilter.appliesToDataSubset && (
                <div className="filter-warning-icon">
                    <span
                        className={icons.warningFilled}
                        title={buildFilterWarning(
                            props.availableFilter.label,
                            dashboardLabels
                        )}
                    />
                </div>
            )}
        </Fields>
    );
}
/* eslint-enable complexity */

FilterTreeSelect.propTypes = {
    availableFilter: PropTypes.instanceOf(Object).isRequired,
    disabled: PropTypes.bool.isRequired,
    filter: PropTypes.instanceOf(Object),
    filterKey: PropTypes.string.isRequired,
    isAdditionalFilter: PropTypes.bool.isRequired,
    isEditor: PropTypes.bool,
    isRequired: PropTypes.bool.isRequired,
    options: PropTypes.instanceOf(Array).isRequired,
    removeAdditionalFilter: PropTypes.func.isRequired,
    showAsterisk: PropTypes.bool.isRequired,
    updateFilter: PropTypes.func.isRequired,
};

FilterTreeSelect.defaultProps = {
    filter: {},
    isEditor: false,
};
